<script lang="ts">
import {defineComponent, onMounted, ref} from "vue";
import {useAuthStore} from "~/plugins/store/auth";
import {useNuxtApp} from "#app";
import {recaptcha} from "~/composables/recaptcha";
import {useReCaptcha} from "vue-recaptcha-v3";
import CustomerPassword from "~/components/customer/CustomerPassword.vue";
import CustomerTermsAgreement from "~/components/customer/CustomerTermsAgreement.vue";
import {
  createDefaultLoginForm,
  createDefaultValidationLoginStatus,
  type LoginForm,
  type ValidationLoginStatus
} from "~/types/User";
import LoadingDialog from "~/components/utils/LoadingDialog.vue";
import {translation, useTranslationStore} from "~/plugins/store/translations";
import type {TranslationList} from "~/types/Utils";

export default defineComponent({
  components: {LoadingDialog, CustomerTermsAgreement, CustomerPassword},
  props: {
    fillEmail: {
      type: String as () => string | null,
      required: false,
    },
    defaultVersion: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  setup(props, {emit}) {
    useHead({
      title: 'Xiga | Login',
    });

    const translationStore = useTranslationStore();
    const translationList = ref<TranslationList>({});
    const fillEmail = ref<string>(props.fillEmail ? props.fillEmail : '');
    const {$toast} = useNuxtApp();
    const showLoadingDialog = ref(false);
    const messageLoadingDialog = ref('');
    const recaptchaInstance = useReCaptcha();
    const authStore = useAuthStore();
    const validationLoginStatus = ref<ValidationLoginStatus>(createDefaultValidationLoginStatus());

    const loginForm = ref<LoginForm>(createDefaultLoginForm());

    if (fillEmail.value) {
      loginForm.value.email = fillEmail.value;
    }

    const loginFields = ['email', 'password', 'terms_agreement'];

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
    });

    const isLoginFormValid = () => {
      let allValid = true;
      loginFields.forEach(field => {
        if (!validationLoginStatus.value[field]) {
          console.log('Validation failed for field: ', field);
          window.dispatchEvent(new CustomEvent('validate-customer', {detail: field}));
          allValid = false;
        }
      });
      return allValid;
    };

    const handleLogin = async () => {
      showLoadingDialog.value = true;
      messageLoadingDialog.value = translation(translationList.value.authenticating, 'Autentificare...');

      if (!isLoginFormValid()) {
        showLoadingDialog.value = false;
        $toast.error(translation(translationList.value.fill_all_fields, 'Te rugăm să completezi toate câmpurile obligatorii corect!'));
        return;
      }
      try {
        const token = await recaptcha(recaptchaInstance);
        await authStore.login({
          email: <string>loginForm.value.email,
          password: <string>loginForm.value.password,
          token
        });
        if (fillEmail.value) {
          emit('close-dialog');
        } else {
          return navigateTo('/');
        }
      } catch (error) {
        console.error(error);
        $toast.error(translation(translationList.value.error_credentials, 'Adresa de email sau parola sunt greșite.'));
      }
      showLoadingDialog.value = false;
    };

    const handleValidationLoginStatus = ({field, isValid}: { field: string, isValid: boolean }) => {
      validationLoginStatus.value[field] = isValid;
    };

    return {
      loginForm,
      handleLogin,
      fillEmail,
      showLoadingDialog,
      messageLoadingDialog,
      handleValidationLoginStatus,
      translationList,
      translation,
      defaultVersion: props.defaultVersion,
    };
  },
});
</script>

<template>
  <v-container class="background-ghost-white" style="max-width: 1200px; border-radius: 15px;">
    <loading-dialog :message="messageLoadingDialog" :show-dialog="showLoadingDialog"/>
    <v-row v-if="defaultVersion" class="my-3">
      <v-col cols="12" sm="auto" md="6" class="d-flex flex-column justify-start centered-on-mobile">
        <h3 class="font-weight-bold">
          {{ translation(translationList.account_already_exists, 'Autentificare') }}</h3>
        <p class="font-weight-regular">
          {{
            translation(translationList.account_already_exists_helper, 'Te rugăm să te autentifici pentru a continua cu contul tău.')
          }}
        </p>
      </v-col>
    </v-row>

    <v-form @submit.prevent="handleLogin">
      <v-row class="d-flex align-center">
        <v-col cols="12" sm="12" md="6">
          <customer-email
              v-model="loginForm.email"
              @validation-customer-status="handleValidationLoginStatus">
            >
          </customer-email>
        </v-col>
        <v-col cols="12" sm="12" md="6">
          <customer-password
              v-model="loginForm.password"
              @validation-customer-status="handleValidationLoginStatus"
          >
          </customer-password>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" sm="12" md="12">
          <customer-terms-agreement
              v-model="loginForm.terms_agreement"
              @validation-customer-status="handleValidationLoginStatus"
          >
          </customer-terms-agreement>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" class="d-flex align-center justify-center">
          <v-btn
              class="block-on-mobile"
              color="primary"
              size="x-large"
              flat
              rounded="lg"
              type="submit"
          >{{ translation(translationList.login, 'Autentificare') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<style scoped>

</style>
